import React, { useState } from 'react';
import { shareFile, shareMultipleFiles, isSharingAvailable } from '../utils/fileSharing';
import { Capacitor } from '@capacitor/core';
import './ShareButton.css';

const ShareButton = ({ 
  url, 
  urls, 
  fileName, 
  fileNames, 
  title = 'Share File',
  className = '',
  children,
  disabled = false,
  showIcon = true,
  variant = 'primary' // 'primary', 'secondary', 'icon-only'
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const isNative = Capacitor.isNativePlatform();
  
  // Determine if we're sharing single or multiple files
  const isMultiple = Array.isArray(urls) && urls.length > 1;
  const fileUrl = isMultiple ? urls[0] : url;
  const fileUrlArray = isMultiple ? urls : [url];
  const fileNameArray = isMultiple ? fileNames : [fileName];

  const handleShare = async () => {
    if (disabled || isLoading) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      let result;
      
      if (isMultiple) {
        result = await shareMultipleFiles(fileUrlArray, fileNameArray, title);
      } else {
        result = await shareFile(fileUrl, fileName, title);
      }
      
      // Don't show error for successful cancellation
      if (!result.success && result.method !== 'cancelled') {
        setError(result.error || 'Failed to share file');
      }
      
      // Clear error after a few seconds (only if there was an actual error)
      if (result.success === false && result.method !== 'cancelled') {
        setTimeout(() => setError(null), 3000);
      }
      
    } catch (error) {
      // Check if this is a cancellation
      if (error.message && (
        error.message.includes('cancelled') || 
        error.message.includes('canceled') ||
        error.message.includes('dismissed') ||
        error.message.includes('User cancelled') ||
        error.message.includes('User canceled')
      )) {
        // Don't show error for cancellation
        return;
      }
      
      setError('An error occurred while sharing the file');
      
      // Clear error after a few seconds
      setTimeout(() => setError(null), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  // Don't render if sharing is not available and we're on web
  if (!isSharingAvailable() && !isNative) {
    return null;
  }

  const getButtonContent = () => {
    if (variant === 'icon-only') {
      return (
        <>
          {showIcon && (
            <svg 
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="currentColor"
              className="share-icon"
            >
              <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"/>
            </svg>
          )}
        </>
      );
    }

    return (
      <>
        {showIcon && (
          <svg 
            width="16" 
            height="16" 
            viewBox="0 0 24 24" 
            fill="currentColor"
            className="share-icon"
          >
            <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"/>
          </svg>
        )}
        {children || (isMultiple ? `Share ${fileUrlArray.length} Files` : 'Share')}
      </>
    );
  };

  return (
    <div className="share-button-container">
      <button
        className={`share-button share-button-${variant} ${className} ${isLoading ? 'loading' : ''}`}
        onClick={handleShare}
        disabled={disabled || isLoading}
        title={isMultiple ? `Share ${fileUrlArray.length} files` : 'Share file'}
      >
        {isLoading ? (
          <>
            <span className="loading-spinner"></span>
            {variant !== 'icon-only' && 'Sharing...'}
          </>
        ) : (
          getButtonContent()
        )}
      </button>
      
      {error && (
        <div className="share-error">
          {error}
        </div>
      )}
    </div>
  );
};

export default ShareButton; 